import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Check, Users, X } from 'lucide-react'

import type { HgAdvisor } from '../../api/client'

const BUTTON_SPRING = { type: 'spring' as const, stiffness: 400, damping: 20 }
const SURFACE_SPRING = { type: 'spring' as const, stiffness: 500, damping: 30 }

/** The assistant's name, wherever the page shows who is speaking. */
export const HONEY = 'Honey'

const initials = (name: string): string =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('')

/** An advisor's picture, or their initials when they have none. */
export const PersonaAvatar: React.FC<{ name: string; imageUrl?: string | null; size?: 'sm' | 'md' }> = ({
    name,
    imageUrl,
    size = 'md',
}) => {
    const box = size === 'sm' ? 'w-5 h-5 text-[10px]' : 'w-7 h-7 text-xs'
    return imageUrl ? (
        <img
            src={imageUrl}
            alt=""
            className={`${box} shrink-0 rounded-full object-cover`}
            data-testid="persona-avatar"
        />
    ) : (
        <span
            className={`${box} shrink-0 rounded-full bg-amber-200 text-amber-900 font-medium inline-flex items-center justify-center`}
            data-testid="persona-avatar"
        >
            {initials(name)}
        </span>
    )
}

interface PersonaPickerProps {
    /** The user's advisors. */
    advisors: HgAdvisor[]
    /** The ones in this chat, in the order they joined. */
    chosen: HgAdvisor[]
    onAdd: (personaId: number) => void
    onRemove: (personaId: number) => void
}

/**
 * The chat's advisors as chips, and a menu of the user's advisors to add or
 * take out (docs/chats.md §9). Renders nothing for a user with none.
 */
const PersonaPicker: React.FC<PersonaPickerProps> = ({ advisors, chosen, onAdd, onRemove }) => {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const chosenIds = new Set(chosen.map((p) => p.persona_id))

    useEffect(() => {
        if (!open) return
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    if (advisors.length === 0) return null

    return (
        <div className="flex items-center gap-1.5 flex-wrap md:justify-end" data-testid="chat-personas">
            <AnimatePresence initial={false}>
                {chosen.map((persona) => (
                    <motion.span
                        key={persona.persona_id}
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={SURFACE_SPRING}
                        style={{ transformOrigin: 'left center' }}
                        className="inline-flex items-center gap-1 rounded-full bg-white/80 pl-0.5 pr-1 py-0.5 text-sm text-gray-700"
                        data-testid="chat-persona-chip"
                    >
                        <PersonaAvatar name={persona.name} imageUrl={persona.image_url} size="sm" />
                        {persona.first_name}
                        <motion.button
                            type="button"
                            onClick={() => onRemove(persona.persona_id)}
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                            transition={BUTTON_SPRING}
                            aria-label={`Remove ${persona.name}`}
                            className="inline-flex text-gray-400 hover:text-gray-700 cursor-pointer"
                        >
                            <X className="w-3.5 h-3.5" />
                        </motion.button>
                    </motion.span>
                ))}
            </AnimatePresence>

            <div className="relative" ref={ref}>
                <motion.button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    transition={BUTTON_SPRING}
                    aria-expanded={open}
                    aria-label="Advisors"
                    title="Advisors"
                    className="inline-flex items-center justify-center rounded-md bg-white/70 p-1.5 text-gray-600 hover:text-gray-900 cursor-pointer"
                    data-testid="chat-personas-button"
                >
                    <Users className="w-4 h-4" />
                </motion.button>
                <AnimatePresence>
                    {open && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -8 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -8 }}
                            transition={SURFACE_SPRING}
                            style={{ transformOrigin: 'top right' }}
                            className="absolute right-0 top-10 z-20 w-64 max-h-[60vh] overflow-y-auto rounded-lg bg-white shadow-lg p-1"
                            role="menu"
                            data-testid="chat-personas-menu"
                        >
                            {advisors.map((advisor) => {
                                const inChat = chosenIds.has(advisor.persona_id)
                                return (
                                    <button
                                        key={advisor.persona_id}
                                        type="button"
                                        role="menuitemcheckbox"
                                        aria-checked={inChat}
                                        onClick={() =>
                                            inChat ? onRemove(advisor.persona_id) : onAdd(advisor.persona_id)
                                        }
                                        className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-blush-light cursor-pointer"
                                    >
                                        <PersonaAvatar name={advisor.name} imageUrl={advisor.image_url} />
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-sm text-gray-800 truncate">{advisor.name}</span>
                                            {advisor.archetype && (
                                                <span className="block text-xs text-gray-500 truncate">
                                                    {advisor.archetype}
                                                </span>
                                            )}
                                        </span>
                                        {inChat && <Check className="w-4 h-4 text-amber-800" />}
                                    </button>
                                )
                            })}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    )
}

export default PersonaPicker
